import React, { useState, useEffect } from 'react';
import {
  SHIP_WINDOW_HOURS,
  parseWindowStart,
  getShipDeadline,
  getProgressPercentage as getWindowProgressPercentage,
  getShippingBand,
} from 'utils/shippingWindow';

interface Props {
  windowStart?: string; // Date string - can be ISO format or "M/D/YY" format
}

export default function ShippingCountdown({ windowStart }: Props) {
  const [timeRemaining, setTimeRemaining] = useState<{
    hours: number;
    minutes: number;
    seconds: number;
    isExpired: boolean;
  }>({ hours: 0, minutes: 0, seconds: 0, isExpired: false });

  useEffect(() => {
    const calculateTimeRemaining = () => {
      // Check if windowStart (shipWindowStart) is provided
      if (!windowStart) {
        setTimeRemaining({ hours: 0, minutes: 0, seconds: 0, isExpired: true });
        return;
      }

      // Parse the initiated date - handle both ISO format and M/D/YY format
      const windowStartMs = parseWindowStart(windowStart);
      if (windowStartMs === null) {
        setTimeRemaining({ hours: 0, minutes: 0, seconds: 0, isExpired: true });
        return;
      }

      // Validate the parsed date
      if (isNaN(windowStartMs)) {
        console.error('Invalid window start:', windowStart);
        setTimeRemaining({ hours: SHIP_WINDOW_HOURS, minutes: 0, seconds: 0, isExpired: false });
        return;
      }

      const deadline = getShipDeadline(windowStartMs);
      const now = new Date().getTime();
      const difference = deadline - now;

      if (difference <= 0) {
        setTimeRemaining({ hours: 0, minutes: 0, seconds: 0, isExpired: true });
        return;
      }

      const hours = Math.floor(difference / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeRemaining({ hours, minutes, seconds, isExpired: false });
    };

    // Calculate immediately
    calculateTimeRemaining();

    // Update every second
    const interval = setInterval(calculateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [windowStart]);

  const formatTime = () => {
    if (timeRemaining.isExpired) {
      return 'Overdue';
    }

    const { hours, minutes, seconds } = timeRemaining;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const getProgressPercentage = () => {
    if (timeRemaining.isExpired) return 0;

    const remainingMs = (timeRemaining.hours * 60 * 60 * 1000) +
                       (timeRemaining.minutes * 60 * 1000) +
                       (timeRemaining.seconds * 1000);

    return getWindowProgressPercentage(remainingMs);
  };

  const getProgressBarClass = () => {
    const band = getShippingBand(getProgressPercentage(), timeRemaining.isExpired);
    return band === 'normal' ? 'progress-bar' : `progress-bar ${band}`;
  };

  return (
    <div className="estimated-delivery">
      <span className="delivery-label">
        {timeRemaining.isExpired ? 'Shipping deadline' : 'Time remaining to ship'}
      </span>
      <div className={`delivery-time ${timeRemaining.isExpired ? 'overdue' : ''}`}>
        {formatTime()}
      </div>
      <div className="delivery-progress">
        <div 
          className={getProgressBarClass()}
          style={{ width: `${getProgressPercentage()}%` }}
        ></div>
      </div>
    </div>
  );
}